import { AdminMessage } from "@components/admin/admin-helpers"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { ShareButton } from "@components/atoms/ShareButton"
import { Breadcrumbs } from "@components/page/Breadcrumbs"
import { Chip } from "@components/ui/Chip"
import { formatNiceDate } from "@utils/date-format"
import { tv } from "@utils/tw"
import { defineBlock, EditableText, usePostEditor, usePostExcerptEditor, usePostTitleEditor } from "eddev/blocks"
import { Link } from "eddev/routing"

export const meta: BlockMeta = {
  title: "Blog Post Header",
  inserter: false,
  icon: "format-aside",
  category: "text",
  flags: {
    fullWidth: true,
  },
  postMetaBlock: {
    postTypes: ["blog"],
    fieldName: "info",
  },
  blockStyles: [
    {
      label: "Large Image",
      name: "large-image",
      isDefault: true,
    },
    {
      label: "Small Image",
      name: "small-image",
    },
    {
      label: "Hidden Image",
      name: "no-image",
    },
  ],
}

export default defineBlock("blog/blog-post-header", (props) => {
  const styles = createStyles()
  return (
    <div className="mb-6">
      <div>
        <Breadcrumbs breadcrumbs={props.blogPost?.breadcrumbs} />
        {props.heroImage ? (
          props.blockStyle !== "no-image" && (
            <div className="grid-auto mb-3 md:hidden">
              <div className={props.blockStyle === "small-image" ? "col-subpage-content-grid" : "col-span-12"}>
                <ResponsiveImage {...props.heroImage} />
              </div>
            </div>
          )
        ) : env.admin ? (
          <div className="w-grid mx-auto md:hidden">
            <AdminMessage>Please upload an cover image for this article</AdminMessage>
          </div>
        ) : null}
        <div className={styles.base()}>
          <div className={styles.heading()}>
            <EditableText
              as="h1"
              className="text-balance max-w-[16em]"
              store={usePostTitleEditor(props.blogPost?.title!)}
              placeholder="Enter a title"
              plainText
            />
          </div>
          <div className={styles.description()}>
            <EditableText
              as="div"
              className="text-balance max-w-[35em]"
              store={usePostExcerptEditor(props.blogPost?.excerpt!)}
              defaultValue="Enter an excerpt"
              plainText
            />
          </div>
          <div className={styles.share()}>
            <ShareButton />
          </div>
          <div className={styles.meta()}>
            {props.category && (
              <div className={styles.tags()}>
                <Link href={props.category?.uri}>
                  <Chip shape="rounded" variant="mid">
                    {props.category?.name}
                  </Chip>
                </Link>
              </div>
            )}
            <div className={styles.date()}>
              {formatNiceDate(env.admin ? usePostEditor().post.date : props.blogPost?.date!)}
            </div>
          </div>
        </div>
      </div>
      {props.heroImage ? (
        props.blockStyle !== "no-image" && (
          <div className="grid-auto mt-6 hidden md:grid">
            <div className={props.blockStyle === "small-image" ? "col-subpage-content-grid" : "col-span-12"}>
              <ResponsiveImage {...props.heroImage} />
            </div>
          </div>
        )
      ) : env.admin ? (
        <div className="w-grid mx-auto hidden md:block">
          <AdminMessage>Please upload an cover image for this article</AdminMessage>
        </div>
      ) : null}
    </div>
  )
})

const createStyles = tv({
  base: "grid-auto pt-3 md:pt-6",
  slots: {
    heading: ["type-title-xl pt-6 pb-5 md:py-0 col-span-12 md:col-span-6 md:mb-10"],
    description: ["type-body-m mb-8 md:mb-0 col-span-12 md:col-span-6"],
    share: ["col-span-12 md:col-span-6"],
    meta: ["col-span-12 -order-3 flex gap-4 md:order-1 md:col-span-6 items-center md:grid-auto"],
    tags: ["md:col-span-3 flex items-center gap-2"],
    date: ["md:col-span-3 type-label-s"],
  },
})
